/*
 * Module for controlling topic searches, including searches by Response, Mapping, Status, and Cat code
 */

define([
  "marionette",
  "backbone",
  "underscore",
  "messaging/messenger",
  "backbone.syphon",
  "modules/topicsSplit/topics/topicSearch/topicSearchView",
  "configuration/properties",
  "modules/topicsSplit/topics/topicSearch/topicSearchModel",
], function (Marionette, Backbone, _, Messenger, BackboneSyphon, TopicSearchView) {
  "use strict";

  return Marionette.Controller.extend({
    initialize: function (options) {
      this.name = "topicSearch";
      this.region = options.region;
    },
    show: function () {
      const statuses = Messenger.reqres.request("kb:statuses");
      const categories = Messenger.reqres.request("kb:categories");
      const attributes = Messenger.reqres.request("kb:attributes");
      const touchpoints = Messenger.reqres.request("kb:touchpoints");
      const languages = Messenger.reqres.request("kb:languages");
      const contexts = Messenger.reqres.request("kb:contexts");
      const defaultTouchpoint = Messenger.reqres.request("default:touchpoint");
      const defaultLanguage = Messenger.reqres.request("default:response:language");
      const kb = Messenger.reqres.request("active:kb:model");
      const topicTypes = Messenger.reqres.request("get:topicTypes");
      const isArchivedAllowed = Messenger.reqres.request("default:showArchivedTopics");
      const responseCollaboration = kb.get("responseCollaboration");
      //we will set this for real once everything is synced.  It might not be loaded yet.
      const topicReview = false;
      // search criteria
      //model used to keep track of the search mode and store search options
      // noinspection JSUnusedGlobalSymbols
      const model = new Backbone.Model({
        topicReview,
        mode: 1,
        touchpoints,
        languages,
        contexts,
        defaultTouchpoint,
        defaultLanguage,
        statuses,
        categories,
        attributes,
        categoryIdArray: [0],
        categoryValueArray: [0],
        topicType: topicTypes.NON_ARCHIVED,
        isArchivedAllowed,
        topicTypes,
        responseCollaboration,
      });
      this.view = new TopicSearchView.topicSearchView({ model: model });
      this.view.on(
        "remove:categories:withEmptyValues",
        function () {
          const categoriesWithoutEmptyValues = new Backbone.Collection();
          categories.each(function (category) {
            if (category.getValues().length > 0) {
              categoriesWithoutEmptyValues.add(category);
            }
          });
          this.view.model.set("categories", categoriesWithoutEmptyValues);
        },
        this,
      );
      this.view.on(
        "click:search",
        function (args) {
          const data = BackboneSyphon.serialize(args.view);
          const topicType = args.model.get("topicType");
          if (data.searchField !== null) {
            const rawSearchValue = data.searchField.trim();
            let searchValue = rawSearchValue.escapeHtml();
            //search must not be empty
            if (searchValue.length > 0) {
              //save before search
              Messenger.save(function (success) {
                if (success) {
                  let searchObj;
                  //perform a different search depending on the search mode we are in
                  switch (this.view.searchMode()) {
                    case this.view.SEARCH_BY_NAME:
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:name",
                        rawSearchValue,
                        topicType,
                      );
                      break;
                    case this.view.SEARCH_BY_EXAMPLE:
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:example",
                        rawSearchValue,
                        this.view.getLanguageChoicesValue(),
                        topicType,
                      );
                      break;
                    case this.view.SEARCH_BY_LABEL:
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:label",
                        rawSearchValue,
                        data.labelLocale,
                        topicType,
                      );
                      break;
                    case this.view.SEARCH_BY_RESPONSE:
                      //get selected touchpoint and language
                      const touchpoint = data.touchpoint;
                      const language = data.language;
                      const matchCase = data.matchCase;
                      const matchWholeWords = data.wholeWord;
                      const searchHtml = data.searchHtml;
                      if (this.view.replaceMode) {
                        const replaceValue = data.replaceField.escapeHtml();
                        searchObj = Messenger.reqres.request(
                          "search:and:replace:responses",
                          searchValue,
                          replaceValue,
                          touchpoint,
                          language,
                          matchCase,
                          matchWholeWords,
                          topicType,
                        );
                      } else {
                        //pass the raw search value because we may need to do an HTML search instead of a plain text search
                        searchObj = Messenger.reqres.request(
                          "search:topics:by:response",
                          rawSearchValue,
                          touchpoint,
                          language,
                          matchCase,
                          matchWholeWords,
                          searchHtml,
                          topicType,
                        );
                      }
                      break;
                    case this.view.SEARCH_BY_CONTENT:
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:associated:content",
                        rawSearchValue,
                        topicType,
                      );
                      break;
                    case this.view.SEARCH_BY_DECISION:
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:decision",
                        rawSearchValue,
                        topicType,
                      );
                      break;
                    case this.view.SEARCH_BY_STATUS:
                      //convert statusId value from string to int
                      searchValue = parseInt(searchValue, 10);
                      //get the status name of the selected status
                      let statusName = _.findWhere(statuses.get("allTopicStatuses"), {
                        statusId: searchValue,
                      }).statusName;
                      //if a date was selected, search for scheduled statuses, otherwise just search for current status
                      if (data.statusDatePicker) {
                        const changeDate = data.statusDatePicker;
                        const timestamp = this.view.getTime();
                        //append time from status name
                        statusName = statusName + " " + changeDate;
                        searchObj = Messenger.reqres.request(
                          "search:topics:by:scheduled:status",
                          searchValue,
                          timestamp,
                          statusName,
                          topicType,
                        );
                      } else {
                        searchObj = Messenger.reqres.request(
                          "search:topics:by:status",
                          searchValue,
                          statusName,
                          topicType,
                        );
                      }
                      break;
                    case this.view.SEARCH_BY_CATEGORY:
                      //get the arrays of category codes and values
                      //they will be sent as strings separated by underscores when the arrays are larger then one
                      const categoryCodes = model.get("categoryIdArray");
                      const categoryCodeValues = model.get("categoryValueArray");
                      //for now, just send the label for the first selected category name/value
                      const category = categories.findWhere({ categoryId: categoryCodes[0] });
                      const categoryName = category.get("categoryName");
                      const values = category.getValues();
                      const categoryValueName = values
                        .findWhere({ categoryValueId: categoryCodeValues[0] })
                        .get("value");
                      if (categoryCodes.length > 1) {
                        searchObj = Messenger.reqres.request(
                          "search:topics:by:categories",
                          categoryCodes.join("_"),
                          categoryCodeValues.join("_"),
                          categoryName,
                          categoryValueName,
                          categoryCodes.length,
                          topicType,
                        );
                      } else {
                        searchObj = Messenger.reqres.request(
                          "search:topics:by:category",
                          categoryCodes[0],
                          categoryCodeValues[0],
                          categoryName,
                          categoryValueName,
                          topicType,
                        );
                      }
                      break;
                    case this.view.SEARCH_BY_SUB_GROUP:
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:sub:group",
                        rawSearchValue,
                        topicType,
                      );
                      break;
                    case this.view.SEARCH_BY_TOPIC_REVIEW:
                      //get the long for the time, not the locale-formatted one
                      searchValue = this.view.getTime();
                      const type = this.view.getType();
                      const assignee = this.view.getAssignee().escapeHtml();
                      const creator = this.view.getCreator().escapeHtml();
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:topic:review",
                        searchValue,
                        assignee,
                        creator,
                        type,
                      );
                      break;
                    case this.view.SEARCH_BY_CONTEXT:
                      //convert context id value from string to int
                      searchValue = parseInt(searchValue, 10);
                      //get the context name of the selected context
                      const contextName = contexts.findWhere({ id: searchValue }).get("status");
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:context",
                        searchValue,
                        contextName,
                        topicType,
                      );
                      break;
                    case this.view.SEARCH_BY_ATTRIBUTE:
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:attributes",
                        rawSearchValue,
                        topicType,
                      );
                      break;
                  }

                  //send the results to be shown in the topics page
                  const topicTypes = Messenger.reqres.request("get:topicTypes");

                  if (topicType === topicTypes.ARCHIVED) {
                    const navBtnsArchived = Messenger.reqres.request(
                      "navigationButtons:search:archived",
                    );
                    Messenger.vent.trigger("topics:search:results", searchObj, navBtnsArchived);
                  } else if (topicType === topicTypes.NON_ARCHIVED) {
                    const navBtns = Messenger.reqres.request("navigationButtons:search");
                    Messenger.vent.trigger("topics:search:results", searchObj, navBtns);
                  } else {
                    const navBtns = Messenger.reqres.request("navigationButtons:search:all");
                    Messenger.vent.trigger("topics:search:results", searchObj, navBtns);
                  }
                }
                console.log(data.searchField);
              }, this);
            } else {
              //save before search
              Messenger.save(function (success) {
                if (success) {
                  let searchObj;
                  let allowableSearch = false;
                  //perform a different search depending on the search mode we are in
                  switch (this.view.searchMode()) {
                    case this.view.SEARCH_BY_TOPIC_REVIEW:
                      //Get the user, if there is one
                      const assignee = this.view.getAssignee().escapeHtml();
                      const creator = this.view.getCreator().escapeHtml();
                      const type = this.view.getType();
                      searchObj = Messenger.reqres.request(
                        "search:topics:by:topic:review",
                        "",
                        assignee,
                        creator,
                        type,
                      );
                      allowableSearch = true;
                      break;
                  }

                  if (allowableSearch) {
                    //send the results to be shown in the topics page
                    const topicTypes = Messenger.reqres.request("get:topicTypes");

                    if (topicType === topicTypes.ARCHIVED) {
                      const navBtnsArchived = Messenger.reqres.request(
                        "navigationButtons:search:archived",
                      );
                      Messenger.vent.trigger("topics:search:results", searchObj, navBtnsArchived);
                    } else if (topicType === topicTypes.NON_ARCHIVED) {
                      const navBtns = Messenger.reqres.request("navigationButtons:search");
                      Messenger.vent.trigger("topics:search:results", searchObj, navBtns);
                    } else {
                      const navBtns = Messenger.reqres.request("navigationButtons:search:all");
                      Messenger.vent.trigger("topics:search:results", searchObj, navBtns);
                    }
                  }
                }
              }, this);
            }
          }
        },
        this,
      );
      this.view.on(
        "set:topicType",
        function (selectedTopicType) {
          model.set("topicType", selectedTopicType);
        },
        this,
      );

      this.view.on(
        "set:isArchivedAllowed",
        function (selectedValue) {
          model.set("isArchivedAllowed", selectedValue);
        },
        this,
      );
      //only show the region once the statuses, categories, touchpoints, languages, kb, and contexts are loaded
      statuses.onSynced(function () {
        categories.onSynced(function () {
          attributes.onSynced(function () {
            touchpoints.onSynced(function () {
              languages.onSynced(function () {
                kb.onSynced(function () {
                  contexts.onSynced(function () {
                    model.set("topicReview", kb.get("taskManagement"));
                    this.showRegion(this.view);
                  }, this);
                }, this);
              }, this);
            }, this);
          }, this);
        }, this);
      }, this);
    },
  });
});
